import { useState, useEffect, useRef } from 'react';
import { getSocket } from '../socket';
import { useToast } from '../context/ToastContext';

// Module-level throttle so the "reload" nudge can't spam, even across remounts
let lastChangeToastAt = 0;
const CHANGE_TOAST_THROTTLE_MS = 15000;

export function usePresence(tripId) {

    const [roster, setRoster] = useState([]);
    const [editingIds, setEditingIds] = useState([]);
    const editTimers = useRef({});
    const { showToast } = useToast();

    useEffect(() => {
        // Create mode has no trip to join
        if (!tripId) return;
        const socket = getSocket();

        const handleUpdate = (people) => setRoster(people || []);

        const handleEditing = (userId) => {
            // Mark them editing (if not already in the list)
            setEditingIds(prev => prev.includes(userId) ? prev : [...prev, userId]);
            // Clears the badge ~3s after their last keystroke
            clearTimeout(editTimers.current[userId]);
            editTimers.current[userId] = setTimeout(() => {
                setEditingIds(prev => prev.filter(id => id !== userId));
                delete editTimers.current[userId];
            }, 3000);
        };

        // Another member saved a change -> nudge this user to reload
        const handleChanged = () => {
            const now = Date.now();
            if (now - lastChangeToastAt < CHANGE_TOAST_THROTTLE_MS) return;
            lastChangeToastAt = now;
            showToast('A member updated this trip — reload to see the latest changes', 'info');
        };

        socket.on('presence:update', handleUpdate);       // listen first
        socket.on('presence:editing', handleEditing);
        socket.on('presence:changed', handleChanged);
        socket.emit('trip:join', tripId);                 // then announce we're here

        return () => {
            socket.emit('trip:leave');                    // leave the room
            socket.off('presence:update', handleUpdate);  // stop listening
            socket.off('presence:editing', handleEditing);
            socket.off('presence:changed', handleChanged);

            Object.values(editTimers.current).forEach(clearTimeout);
            editTimers.current = {};
        };
    }, [tripId]);

    return { roster, editingIds };
}